import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Award, Users, BookOpen, Globe } from 'lucide-react';

const AboutUs = () => {
  const milestones = [
    { year: '1951', title: 'Foundation', description: 'JG Group established with a vision of educational excellence' },
    { year: '1975', title: 'Silver Jubilee', description: 'Celebrated 25 years of remarkable growth' },
    { year: '2000', title: 'Golden Era', description: 'Expanded to multiple campuses and programs' },
    { year: '2025', title: 'Amrut Mahotsav', description: 'Celebrating 75 glorious years of service' },
  ];

  const stats = [
    { icon: <Users size={40} />, value: '50,000+', label: 'Alumni' },
    { icon: <BookOpen size={40} />, value: '100+', label: 'Programs' },
    { icon: <Award size={40} />, value: '500+', label: 'Awards' },
    { icon: <Globe size={40} />, value: '2+', label: 'States' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[400px] mt-20" data-testid="about-hero">
        <img
          src="https://images.unsplash.com/photo-1687709644312-8c67bd4821c6"
          alt="JG Group"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">About Us</h1>
            <p className="text-xl text-gray-200">75 Years of Excellence in Education</p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-spacing" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-800">
              Our <span className="text-gradient">Story</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-green-600 mx-auto mb-8"></div>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Shri J. G. Co-Operative Hospital and Research Institute, Ghataprabha is a state level Charitable Co-operative Society and a trust established in the year 1951. We are providing satisfactory and high quality health services in Medical, Nursing, Education and Social services to rural and needy people.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Ist and 2nd delivery services, Immunization services to children and mothers and family welfare services including sterilization operations are free.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center hover-lift" data-testid={`stat-${index}`}>
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-green-600 text-white mb-4">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="section-spacing bg-gradient-to-br from-orange-50 to-white" data-testid="milestones-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-gray-800">
              Our <span className="text-gradient">Milestones</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-green-600 mx-auto"></div>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-orange-500 to-green-600 hidden md:block"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} flex-col`} data-testid={`milestone-${index}`}>
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} text-center`}>
                    <div className="bg-white rounded-xl shadow-lg p-6 hover-lift">
                      <div className="text-3xl font-bold text-orange-600 mb-2">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-full md:w-2/12 flex justify-center my-4 md:my-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-green-600 border-4 border-white shadow-lg"></div>
                  </div>
                  <div className="w-full md:w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-spacing" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 md:p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">Our Vision</h3>
              <p className="text-lg leading-relaxed text-white/90">
                The vision of our institution is to actively pioneer the students with highest quality of traditional values of Ayurveda. We always strive to provide advanced, modern, scientifically proven teaching through innovative research so that it can meet the needs of nation in preparation of medicine and management of diseases through Ayurveda.
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 md:p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <p className="text-lg leading-relaxed text-white/90">
                Our main aim is to provide excellent and traditional ayurvedic medical education by a team of dedicated and highly skilled teaching faculty. We also aims at giving high quality ayurvedic treatment through well experienced and professional doctors.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;